// src/components/AnnotatedText.jsx
//
// In-reader glossary/annotation highlighting (the deferred feature). A reader provides a
// precomputed term MATCHER via AnnotationsContext; AnnotatedText wraps the FIRST
// occurrence of each matched glossary/annotation term in a section's prose with an
// anchored, tappable popover (the same GlossTerm used for examiner notes). Section
// renderers just wrap their text fields in <AnnotatedText>{text}</AnnotatedText> -- no
// prop threading. Cheap: ONE precompiled regex per terms set (built once, in the reader).
import React, { createContext, useContext, Fragment } from 'react';
import GlossTerm from './InlineGlossPopover.jsx';
import { buildTermMatcher } from './termMatcher.js';
import { terminologyExamTip } from './terminologyView.js';

// Carries a { regex, byLower } matcher (or null). null -> AnnotatedText is a pass-through.
export const AnnotationsContext = createContext(null);

// Re-export the pure matcher builder so readers can import it from here.
export { buildTermMatcher };

// Exported for <RichText> (authoring-5b), which shows the SAME popover for a term it
// finds in a markdown text node.
export function TermPopover({ term }) {
    return (
        <>
            <span className="block font-display text-book-accent text-xs mb-1 font-semibold">{term.name}</span>
            {term.definition && <span className="block font-serif text-book-text text-sm leading-relaxed">{term.definition}</span>}
            {term.example && <span className="block font-serif italic text-book-muted text-xs mt-1.5">e.g. {term.example}</span>}
            {term.tip && <span className="block font-serif text-book-muted text-xs mt-1.5">Tip: {terminologyExamTip(term.tip)}</span>}
        </>
    );
}

// Wrap glossary/annotation matches in `children` (a plain string) with a GlossTerm
// popover -- FIRST occurrence per distinct term only (keeps prose readable). Non-string
// children, no matcher, or no match -> returns the original unchanged.
export default function AnnotatedText({ children }) {
    const matcher = useContext(AnnotationsContext);
    if (!matcher || typeof children !== 'string' || !children) return children;
    const { regex, byLower } = matcher;
    regex.lastIndex = 0;
    const parts = [];
    const seen = new Set();
    let last = 0, key = 0, m;
    while ((m = regex.exec(children)) !== null) {
        const lower = m[0].toLowerCase();
        const term = byLower.get(lower);
        if (!term || seen.has(lower)) continue; // first occurrence of each term only
        seen.add(lower);
        if (m.index > last) parts.push(<Fragment key={key++}>{children.slice(last, m.index)}</Fragment>);
        parts.push(<GlossTerm key={key++} label={'Definition: ' + term.name} popover={<TermPopover term={term} />}>{m[0]}</GlossTerm>);
        last = m.index + m[0].length;
    }
    if (!parts.length) return children;
    parts.push(<Fragment key={key++}>{children.slice(last)}</Fragment>);
    return <>{parts}</>;
}
